import { Alert, Button, Card, SkeletonText, Spinner } from '@ashniva/ui';

import { formatDateTime } from '../../../shared/lib/format';
import { useQuestionQuery } from '../api-questions';
import { QUESTION_STATUS_LABELS } from '../ri-text';
import { useWorkspace } from '../workspace-context';
import { AnswerView } from './AnswerView';
import { EvidencePanel } from './EvidencePanel';
import { ProblemNotice } from './ProblemNotice';

/**
 * One question, followed until it is answered: its progress (Repository Intelligence cannot say how far along it is,
 * so the step it is at and since when), "still working" after two minutes with a button to look again, then the
 * answer and its evidence. Progress is announced politely to screen readers as it changes.
 */
export function QuestionView({ questionId }: { questionId: string }) {
  const { linkId } = useWorkspace();
  const { query, stillWorking, checkAgain } = useQuestionQuery(linkId, questionId);

  if (query.isLoading) {
    return (
      <Card title="Answer">
        <div aria-busy="true">
          <span className="sr-only">Loading the question</span>
          <SkeletonText lines={5} />
        </div>
      </Card>
    );
  }
  if (query.isError || !query.data) {
    return (
      <Card title="Answer">
        <ProblemNotice error={query.error} onRetry={() => void query.refetch()} />
      </Card>
    );
  }
  const result = query.data;
  const final = result.question.pollAfterMs === null;
  return (
    <Card title="Answer">
      <div className="ri-stack">
        <p role="status" aria-live="polite" className="ri-state__line">
          {final ? null : <Spinner size="sm" decorative />}
          {QUESTION_STATUS_LABELS[result.question.status]}
          {final ? null : (
            <span className="ri-muted">
              {' '}
              · asked at {formatDateTime(result.question.createdAt)}
            </span>
          )}
        </p>
        {stillWorking ? (
          <Alert
            tone="info"
            title="Still working"
            action={<Button onClick={checkAgain}>Check again</Button>}
          >
            This is taking longer than usual. Desk stopped checking so as not to keep asking; look
            again in a moment.
          </Alert>
        ) : null}
        {final ? (
          <>
            <AnswerView result={result} />
            <EvidencePanel evidence={result.evidence} />
          </>
        ) : null}
      </div>
    </Card>
  );
}
